<template>
  <b-card
    id="chat-window"
    class="v-flex"
    no-body
    :footer-class="{
      'has-compliance-banner': showComplianceBlock || showComplianceHint
    }"
  >
    <div id="chat-toolbar">
      <b-input-group id="search-chat-group" size="sm">
        <b-form-input
          v-model="search"
          placeholder="Search chats..."
          type="text"
          data-cy="search-chat"
          @keyup.enter="searchChat"
        />
        <b-input-group-append is-text>
          <b-icon icon="search" @click="searchChat" />
        </b-input-group-append>
      </b-input-group>
      <ChatExport :customer="customer" />
    </div>

    <b-card-body id="chat-messages" ref="messageList" class="v-flex rounded-0">
      <infinite-loading
        direction="top"
        :identifier="infiniteId"
        @infinite="load"
      >
        <template #no-more>
          <span class="no-more">Beginning of Conversation</span>
          <hr />
        </template>
      </infinite-loading>
      <div class="date-divider"></div>
      <ChatMessage
        v-for="(value, index) in messages"
        :key="value.id"
        :message="value"
        :show-date="
          index === 0 || showDate(messages[index - 1].created, value.created)
        "
        class="message"
        :show-icon="true"
        :last-message="messages[index - 1]"
        :type="value.sender ? 'inbound' : 'outbound'"
        :customer="customer"
        :language="language"
      />
    </b-card-body>

    <!-- Sits between the thread and the footer: the footer keeps its fixed height, so the
         composer never moves, and the thread gives up exactly this much height instead. -->
    <div
      v-if="showComplianceBlock || showComplianceHint"
      class="card-warning-slot"
    >
      <div
        v-if="showComplianceBlock"
        class="card-warning-block"
        role="alert"
        aria-live="assertive"
        data-cy="chat-compliance-error"
      >
        <div class="card-warning-head">
          <ShieldAlert :size="18" :stroke-width="1.75" />
          <span class="card-warning-title">Message not sent</span>
        </div>
        <p class="card-warning-body">{{ cardComplianceError }}</p>
        <div class="card-warning-actions">
          <button
            type="button"
            class="card-warning-dismiss"
            @click="dismissComplianceBlock"
          >
            Got it
          </button>
        </div>
      </div>
      <div
        v-else
        class="card-warning-hint"
        aria-live="polite"
        data-cy="chat-compliance-hint"
      >
        <TriangleAlert :size="16" :stroke-width="1.5" />
        <span>{{ complianceHint }}</span>
      </div>
    </div>

    <template #footer>
      <div
        v-if="accountId && !isChatDisabled"
        id="snippet-triggers"
        class="rack compress"
      >
        <TemplatePicker
          id="message-templates"
          :templates="messageTemplates"
          :disabled="renderTemplateBusy"
          dropup
          @select="messageTemplatesSelected"
        >
          <template #button-content>
            <Mail v-if="!renderTemplateBusy" />
            <span v-else class="spinner">
              <b-spinner small />
            </span>
          </template>
        </TemplatePicker>

        <SnippetTrigger
          label="Payment Link"
          type="dark"
          data-cy="payment-link-snippet"
          @click.native="addPaymentLink"
        />

        <!-- v-if on the wrapper, not the button: `.rack > div` pads every direct
             child 10px a side, so an empty wrapper still spent 20px of the row and
             pushed the language select away from the Payment Link pill. -->
        <div v-if="!canFreeType">
          <b-button id="clear-chat" small @click="clearChat">
            <CircleX :size="20" />
          </b-button>
        </div>

        <b-form-group v-if="languages.size > 1">
          <b-form-select
            id="language"
            v-model="language"
            size="sm"
            :options="options(languages)"
          />
        </b-form-group>
      </div>

      <b-form
        id="chat-input"
        :class="{
          'chat-input-disabled': !canFreeType || isChatDisabled,
          'chat-input-blocked': showComplianceBlock
        }"
        @keyup="handleKey"
        @change="handleChange"
      >
        <b-textarea
          id="chat-input-text"
          v-model="messageBody"
          :placeholder="chatDisplay"
          :maxlength="1500"
          no-resize
          :disabled="!canFreeType || isChatDisabled"
        />
        <small v-if="messageBody.length >= 1500" class="chat-limit-warning">
          Message limited to 1500 characters.
        </small>

        <b-button
          v-if="!isChatDisabled"
          id="chat-send"
          data-cy="send-chat-customer"
          :disabled="isSending"
          @click="send"
        >
          <Send :size="20" />
        </b-button>

        <div class="actions">
          <label v-if="!isChatDisabled" class="btn btn-sm" for="attach-file">
            <Paperclip :size="20" />
          </label>
        </div>

        <div v-if="file" class="img-preview">
          <b-button size="sm" @click="clearAttachment">
            <CircleX :size="20" />
          </b-button>
          <img v-if="fileUrl" :src="fileUrl" />
          <Spinner v-else :size="20" />
        </div>

        <div style="display: none">
          <b-form-file
            id="attach-file"
            v-model="file"
            accept="image/*, .pdf"
            hidden
            @input="uploadMedia"
          />
        </div>
      </b-form>
    </template>
  </b-card>
</template>

<script>
import { AuthMixin } from '@/mixins/AuthMixin/AuthMixin'
import { isInboundChatMessage, isNewChatMessage } from '@/utils/functions'
import {
  checkCardCompliance,
  CARD_COMPLIANCE_HINTS,
  CARD_COMPLIANCE_MESSAGES
} from '@/utils/card-compliance'
import { reportBlockedSend } from '@/utils/blocked-send-report'
import axios, { cfAxios } from '@/utils/init-axios.js'
import endpoints from '@/utils/endpoints.js'
import { uniqBy } from 'lodash-es'
import moment from 'moment'
import ReconnectingWebSocket from 'reconnecting-websocket'
import SnippetTrigger from '@/components/SnippetTrigger/SnippetTrigger/SnippetTrigger.vue'
import InfiniteLoading from 'vue-infinite-loading'
import ChatMessage from '@/components/Messages/ChatMessage.vue'
import ChatExport from '@/components/Messages/ChatExport.vue'
import TemplatePicker from '@/components/MessageTemplate/TemplatePicker/TemplatePicker.vue'
import {
  Mail,
  CircleX,
  Send,
  Paperclip,
  LoaderCircle,
  ShieldAlert,
  TriangleAlert
} from 'lucide-vue'

import {
  Component,
  VModel,
  Mixins,
  Watch,
  Prop,
  Vue
} from 'vue-property-decorator'
import { mapGetters } from 'vuex'
import VueMoment from 'vue-moment'
Vue.use(VueMoment)

const RESTRICTED_STATUSES = [
  'NO_COMMUNICATION',
  'DO_NOT_CONTACT',
  'BANKRUPT',
  'INACTIVE'
]

@Component({
  computed: {
    ...mapGetters(['currentAccount'])
  },
  methods: {
    moment,
    showDate(date1, date2) {
      return date1.format('YYYY-MM-DD') !== date2.format('YYYY-MM-DD')
    }
  },
  components: {
    ChatExport,
    ChatMessage,
    InfiniteLoading,
    SnippetTrigger,
    TemplatePicker,
    Mail,
    CircleX,
    Send,
    Paperclip,
    LoaderCircle,
    ShieldAlert,
    TriangleAlert
  }
})
export default class Chat extends Mixins(AuthMixin) {
  @VModel() customer
  @Prop({ type: String, default: '' }) accountId
  @Prop({ type: String, default: null }) accountStatus
  @Prop({ type: Object, default: null }) account

  file = null
  fileUrl = null
  hasDraft = false
  infiniteId = +new Date()
  isSending = false
  language = 'en-US'
  languages = new Set(['en-US'])
  messageBody = ''
  messageTemplates = []
  messages = []
  page = 1

  renderTemplateBusy = false
  search = null
  socket = null

  async addPaymentLink() {
    const accountId = this.accountId
    const merchantID = this.$route.params.merchantID
    const response = await axios.post(
      this.$endpoints.createAccountPaymentLink(merchantID, accountId),
      {
        customer_id: this.customer.id
      }
    )
    const paymentLink = response.data.payment_link
    this.messageBody = this.messageBody + paymentLink
  }

  async messageTemplatesSelected(event) {
    this.renderTemplateBusy = true

    try {
      const response = await this.axios.post(
        endpoints.renderMessageTemplate(
          this.$route.params.merchantID,
          event.id
        ),
        {
          customer: this.customer.id,
          account: this.currentAccount.id || this.customer.accounts[0].id,
          type: 'TEXT'
        }
      )

      this.messageBody = this.messageBody + response.data.rendered_body + ' '
    } catch (err) {
      console.log('error', err)
    } finally {
      this.renderTemplateBusy = false
    }
  }

  created() {
    this.initiateConnections()
    this.getMessageTemplates()
  }

  @Watch('customer')
  onCustomerChange(newCustomer, oldCustomer) {
    const newId = newCustomer?.id
    const oldId = oldCustomer?.id
    if (newId === oldId) return
    this.search = null
    this.messageBody = ''
    this.file = null
    this.fileUrl = null
    this.hasDraft = false
    this.isSending = false
    this.$emit('chatDraft', false)
    this.clear()
    this.initiateConnections()
  }

  clear() {
    this.page = 1
    this.infiniteId += 1
    this.messages = []
  }

  @Watch('$route.params.customerID')
  clearChat() {
    this.messageBody = ''
  }

  beforeDestroy() {
    if (this.socket) this.socket.close()
  }

  get canFreeType() {
    if (this.$store.getters.currentMerchant.check_custom_roles) {
      return this.hasPermission([
        'super_admin',
        'admin',
        'is_customer_free_text_allowed'
      ])
    } else {
      return this.hasPermission(['super_admin', 'admin', 'staff'])
    }
  }

  connect() {
    if (this.socket) this.socket.close()

    const base =
      process.env.VUE_APP_WS_URL ||
      (typeof window !== 'undefined' && window.VUE_APP_WS_URL) ||
      ''
    const id = this.customer.id
    const token = this.$store.getters.accessToken
    const url = `${base}/ws/chat/${id}/?token=${token}`

    this.socket = new ReconnectingWebSocket(url)
    this.socket.onmessage = event => {
      let payload
      try {
        payload = JSON.parse(event.data)
      } catch (err) {
        console.warn('Chat: dropped malformed WS frame', err)

        return
      }
      const message = payload?.message
      if (!message) return
      const parsed = this.parseMessage(message)
      if (!isNewChatMessage(this.messages, parsed)) return
      this.messages.push(parsed)
      if (isInboundChatMessage(parsed)) {
        this.$emit('inbound-message')
      }
    }
  }

  clearAttachment() {
    this.file = null
    this.fileUrl = null
  }

  async getMessageTemplates() {
    try {
      const response = await cfAxios.get(
        endpoints.merchantMessageTemplates(
          this.$store.getters.currentMerchant?.pkid
        )
      )
      const activeTemplates = response.data.templates.filter(
        template => template.active && template.chat_visible
      )
      this.messageTemplates = activeTemplates.map(template => {
        return { value: template, text: template.name }
      })
    } catch (err) {
      console.log('error', err)
    }
  }

  handleKey(event) {
    this.hasDraft = this.messageBody !== ''

    if (event.key === 'Enter' && !event.shiftKey) {
      if (this.cardComplianceError) {
        // keydown already inserted the newline the send would have consumed
        this.messageBody = this.messageBody.replace(/\n$/, '')
        this.blockSend()

        return
      }
      this.send()
      event.preventDefault()
    }
  }

  handleChange() {
    this.hasDraft = this.messageBody !== ''
    this.$emit('chatDraft', this.hasDraft)
  }

  initiateConnections() {
    if (!this.customer?.id) return
    this.languages = new Set(['en-US'])

    this.connect()
  }

  async load(state) {
    try {
      if (!this.customer?.id) {
        state.complete()

        return
      }
      const response = await this.axios.get(
        endpoints.listCustomerMessages(
          this.$route.params.merchantID,
          this.customer.id
        ),
        {
          params: {
            page: this.page,
            page_size: 25,
            ...(this.search && { search: this.search.trim() })
          }
        }
      )

      const parsed = response.data.results
        .map(message => this.parseMessage(message))
        .reverse()

      this.messages = uniqBy([...parsed, ...this.messages], 'id')
      this.page += 1
      state.loaded()

      if (!response.data.next) state.complete()
    } catch {
      state.complete()
    }
  }

  searchChat() {
    // this triggers the load() function, that does the actual search
    this.clear()
  }

  retryLoad() {
    if (this.messages.length === 0 && this.customer?.id) {
      this.clear()
    }
  }

  reloadMessages() {
    this.clear()
  }

  parseMessage(message) {
    const parsed = {
      id: message.id,
      sender: message.sender,
      text: message.body,
      language: message.language,
      translations: message.translations,
      created: moment(message.created),
      attachments: message?.attachments,
      error: message?.error_code,
      errorMessage: message?.error_message,
      secondaryMessage: message?.secondary_message,
      status: message?.status
    }

    this.languages.add(parsed.language)

    if (
      message.translations &&
      typeof message.translations === 'object' &&
      !Array.isArray(message.translations)
    ) {
      Object.entries(message.translations).forEach(([language]) => {
        this.languages.add(language)
      })
    }

    return parsed
  }

  options(values) {
    const languages = [...values]
    const names = new Intl.DisplayNames(languages, { type: 'language' })

    // Label off the base subtag so en-US reads "English" rather than "American English" — the full name is
    // wide enough to wrap this row onto a second line in the customer panel. Only when the base is
    // unambiguous: two regional variants of one language would otherwise render as the same label.
    const baseOf = language => language.split('-')[0]
    const bases = languages.map(baseOf)
    const options = languages.reduce((current, language) => {
      const base = baseOf(language)
      const unique = bases.filter(b => b === base).length === 1
      current.push({
        value: language,
        text: names.of(unique ? base : language)
      })

      return current
    }, [])

    return options
  }

  // Asking a customer for card details, or texting a card number, is out of PCI scope for SMS. Computed
  // from the draft itself so template inserts and payment links are covered, not just keystrokes.
  get cardComplianceViolation() {
    return checkCardCompliance(this.messageBody)
  }

  get cardComplianceError() {
    const violation = this.cardComplianceViolation

    return violation ? CARD_COMPLIANCE_MESSAGES[violation] : null
  }

  get complianceHint() {
    const violation = this.cardComplianceViolation

    return violation ? CARD_COMPLIANCE_HINTS[violation] : null
  }

  sendBlocked = false

  get showComplianceBlock() {
    return this.sendBlocked && Boolean(this.cardComplianceViolation)
  }

  get showComplianceHint() {
    return Boolean(this.cardComplianceViolation) && !this.sendBlocked
  }

  @Watch('cardComplianceViolation')
  onCardComplianceViolation(violation) {
    if (!violation) this.sendBlocked = false
  }

  dismissComplianceBlock() {
    this.sendBlocked = false
  }

  // A refused Send is reported so the account team hears about it (Slack, via messaging-service).
  // Once per refusal: holding Enter or clicking Send again while the panel is up must not repeat
  // the alert. Editing back to clean text clears sendBlocked, so the next refusal reports again.
  blockSend() {
    if (this.sendBlocked) return
    this.sendBlocked = true
    const merchant = this.$store.getters.currentMerchant
    reportBlockedSend({
      merchantId: merchant?.pkid ?? merchant?.id,
      violation: this.cardComplianceViolation,
      message: this.messageBody,
      composer: 'chat',
      customerId: this.customer?.id ?? null,
      accountId: this.accountId || null
    })
  }

  async send() {
    if (this.isSending) return
    if (this.cardComplianceError) {
      this.blockSend()

      return
    }
    const text = this.messageBody.trim()
    if (!(text && text.length > 0) && !this.fileUrl) return
    this.isSending = true

    const customerId = this.customer.id
    const merchantID = this.$route.params.merchantID
    const accountId = this.accountId
    const fileUrl = this.fileUrl
    const fileType = this.file ? this.file.type : null

    let message = {
      id: null,
      sender: 'me',
      text: text,
      language: 'en-US',
      created: moment(),
      translations: {},
      pending: true
    }

    this.messageBody = ''
    const index = this.messages.push(message) - 1

    this.hasDraft = false
    this.$emit('chatDraft', this.hasDraft)

    try {
      const response = await axios.post(
        endpoints.sendMessage(merchantID, accountId),
        {
          customer: customerId,
          message: text,
          file_url: fileUrl,
          content_type: fileType
        }
      )

      this.file = null
      this.fileUrl = null

      if (this.customer?.id !== customerId) return

      message = response.data[customerId]
      const parsed = this.parseMessage(message)
      Vue.set(this.messages, index, parsed)

      this.$nextTick(() => {
        const element = this.$refs.messageList?.$el ?? this.$refs.messageList
        if (element) {
          element.scrollTop = element.scrollHeight
        }
      })
    } finally {
      this.isSending = false
    }
  }

  openImage(url) {
    window.open(url, '_blank')
  }

  async uploadMedia() {
    this.fileUrl = null
    const file = this.file
    if (file === null) return

    // in MiB
    const fileSize = this.file.size / 1024 / 1024
    if (1 < fileSize) {
      this.file = null
      this.fileUrl = null
      this.$toasted.global.error({
        message: 'File must be less than 1mb in size'
      })

      return
    }
    // Silly workaround for how the chat API is currently implemented.
    const accountId = this.customer.accounts[0].id
    const formData = new FormData()
    formData.append('file', this.file)
    const response = await axios.post(
      endpoints.uploadMMSMedia(this.$route.params.merchantID, accountId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    )

    this.fileUrl = response.data.media_path
  }

  vcard(type) {
    return type === 'text/vcard' || type === 'text/x-vcard'
  }

  get chatDisplay() {
    if (this.isChatDisabled) {
      return this.chatDisabledReason
    }

    return this.canFreeType
      ? 'Type here to chat...'
      : 'Choose a template to chat...'
  }

  get isChatDisabled() {
    const status = this.accountStatus ?? this.account?.status
    if (status && RESTRICTED_STATUSES.includes(status)) {
      return true
    }

    const textOptin = this.customer?.optins?.find(optin => {
      return optin.category === 'PAYMENT' && optin.media === 'TEXT'
    })

    return !textOptin?.status
  }

  get chatDisabledReason() {
    const status = this.accountStatus ?? this.account?.status
    if (status && RESTRICTED_STATUSES.includes(status)) {
      const statusMap = {
        NO_COMMUNICATION: 'No Communication',
        DO_NOT_CONTACT: 'Do Not Contact',
        BANKRUPT: 'Bankrupt',
        INACTIVE: 'Inactive'
      }

      return `Chat disabled: Account status is "${statusMap[status] || status}"`
    }

    const textOptin = this.customer?.optins?.find(optin => {
      return optin.category === 'PAYMENT' && optin.media === 'TEXT'
    })

    if (!textOptin?.status) {
      return 'Chat disabled: Customer has not opted in to text for Collections & Payments'
    }

    return ''
  }
}
</script>

<style lang="scss">
#message-templates {
  svg,
  .spinner {
    width: 2rem;
    height: 1.5rem;
  }
  // Only the mail-icon toggle takes the button chrome — the menu's options are
  // buttons too now that the list is searchable.
  > .dropdown-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.1em 0.25em;

    background: $steel-gray;
  }
  // Menu chrome — width, list height, row padding — belongs to TemplatePicker
  // so every mount site behaves the same. Chat only sets the chat-specific
  // hover tint and keeps the list flush: a gray ul behind white rows drew a
  // hairline between every option.
  ul {
    font-size: 1em;

    background: white;

    li button:hover {
      background: $sms-header-end-gradient;
      cursor: pointer;
    }
  }
}

#clear-chat {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.1em;

  background: #8193a9;
  border-radius: 100%;

  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
}
</style>

<style lang="scss" scoped>
// Search + Export, top-right over the message list. The export stays OUTSIDE the
// input group: the group's `* { background: #fff }` reskins anything inside it (the
// button went pale with white corners when it lived there).
#chat-toolbar {
  position: absolute;
  top: 1rem;
  right: 1.5rem;
  z-index: 2;

  display: flex;
  gap: 0.5rem;
  align-items: center;
  width: 70%;
  max-width: 360px;
}

#search-chat-group {
  flex: 1;
  min-width: 0;

  * {
    color: #2c3e50;
    background: #fff;
    border-color: #d0d7de;
  }

  input {
    height: 32px;
    padding: 0 0.65rem;
    font-size: 13px;
    border-right: 0;
    border-top-left-radius: 6px;
    border-bottom-left-radius: 6px;
    transition:
      background 0.15s,
      border-color 0.15s;

    &::placeholder {
      color: #6c757d;
    }

    &:focus {
      background: #fff;
      border-color: #adb5bd;
      box-shadow: none;
      outline: none;
    }
  }

  .input-group-append {
    border-left: 0;
    border-top-right-radius: 6px;
    border-bottom-right-radius: 6px;

    .input-group-text {
      padding: 0 0.65rem;
      color: #495057;
      background: #fff;
      border-color: #d0d7de;
      cursor: pointer;
      transition:
        background 0.15s,
        color 0.15s;

      svg,
      .b-icon {
        background: transparent;
      }

      &:hover {
        color: #212529;
        background: #f1f3f5;
      }
    }
  }
}

#snippet-triggers {
  font-size: 0.9rem;

  // Keep this row at its natural height and let #chat-input absorb the difference.
  flex: 0 0 auto;
  // `.rack` wraps. The shortened label ("English") fits this line in the 23.5em
  // customer panel and in the inbox. A full regional name ("American English") does
  // not: `nowrap` held it on this line and clipped the caret on the panel edge.
  // The footer is a column, so a wrapped select shrinks #chat-input instead of
  // covering it.
  width: 100%;
  min-width: 0;
  max-width: 100%;
  align-items: center;
  // Real gap above the message box. The pill's own 10px bottom margin used to be the
  // only thing here, and it spaced the row by pushing one child off-centre — see below.
  margin-bottom: 12px;

  > div {
    flex-shrink: 0;
  }

  // SnippetTrigger carries `margin-bottom: 10px` for MessageTemplateEdit, where the
  // pills stack in rows. In a single centred row it just makes the pill's box 10px
  // taller at the bottom, so the visible pill rides ~5px above the mail button and the
  // select. Drop it here only; the stacked mount sites still need it.
  .trigger > div {
    margin-bottom: 0;
  }

  .form-group {
    // The form-group's default 1rem bottom margin is dead space inside a footer that
    // is already height-capped.
    margin-bottom: 0;
    // Hold the select at its content width. Shrinking it clipped "English". A name
    // wider than the row wraps (see above) instead of painting past the panel.
    // max-width keeps a parent narrower than the select from overflowing the footer.
    flex: 0 0 auto;

    select {
      width: auto;
      max-width: 100%;
    }
  }
}

.message-info {
  color: white;
}

#chat-header {
  align-items: center;
  justify-content: space-between;

  * {
    padding-bottom: none;
  }
}

#chat-window {
  width: 100%;
  max-height: 100%;

  border: none;
  border-top: 1px solid gray;

  .no-more {
    color: white;
  }

  .card {
    flex: 1;
    max-height: 100%;

    border: none;
    border-radius: 0;

    .card-header {
      padding: 0.25em;

      color: $blue;

      background: $customer-detail-light-bg;
      border-bottom-style: none;
    }
  }

  .card-body {
    height: 100%;
    overflow: hidden;

    background: $customer-detail-light-bg;
  }

  // Column flex so #snippet-triggers and #chat-input share the fixed height instead
  // of both measuring against it. Without this, #chat-input's `height: 100%` resolved
  // to the whole footer no matter how tall the row above it got, so the language
  // select pushed the textarea's bottom edge — and the paperclip pinned to it — out
  // of view.
  .card-footer {
    display: flex;
    flex-direction: column;
    height: 330px;
    // Bottom padding was 65px, sized to swallow the overflow the bug produced rather
    // than to space anything. Now that the children are sized honestly it is real
    // empty space, so it matches the top and the input box keeps the height it has
    // always rendered at.
    padding: 24px 30px 24px 24px;

    background: #455567;
    border-top: 1px solid #6a84a0;
  }

  // The banner slot above carries the footer's top border and background, so the footer drops
  // its own and closes the gap while a banner shows.
  .card-footer.has-compliance-banner {
    padding-top: 12px;
    border-top: 0;
  }

  .card-warning-slot {
    flex: 0 0 auto;
    padding: 12px 30px 0 24px;
    background: #455567;
    border-top: 1px solid #6a84a0;
  }

  #chat-input {
    position: relative;

    display: flex;
    flex-direction: column;
    // Takes whatever the row above leaves rather than claiming the footer's full
    // height. min-height: 0 lets it shrink past the textarea's intrinsic size —
    // without it a flex item floors at its content and the clipping comes back.
    flex: 1;
    min-height: 0;

    background-color: #373c42;
    border-radius: 0.25rem;

    transition:
      background-color 0.2s ease,
      box-shadow 0.2s ease;

    #chat-input-text {
      flex: 1;
      width: calc(100% - 2rem);
      overflow: auto;

      color: white;
      font-size: 14px;
      line-height: 1.33;
      white-space: pre-wrap;
      word-wrap: break-word;

      background-color: transparent;
      border-style: none;
      outline: none;
      -webkit-font-smoothing: antialiased;

      &::placeholder {
        color: #ccc;
      }
    }

    #chat-input-text:empty::before {
      display: block;

      outline: none;
      cursor: text;
      filter: contrast(15%);

      content: attr(placeholder);
    }

    button#chat-send {
      align-self: center;
      margin-right: 0.5em;
      margin-left: auto;

      text-align: center;

      background-color: $button-blue;
      border-radius: 50%;
      svg {
        margin-left: -3px;
      }
    }

    .img-preview {
      position: relative;

      display: flex;
      align-items: center;
      justify-content: center;
      width: 3.125rem;
      height: 3.125rem;
      margin: 0.1rem;
      margin-top: 0.5rem;

      color: $customer-detail-text-color;

      border: 1px solid $customer-detail-text-color;
      border-radius: 0.2rem;

      .btn {
        position: absolute;
        top: 0;
        right: 0;

        color: $customer-detail-error-text-color;

        background-color: transparent;
        border-style: none;
        transform: translate(50%, -50%);

        &:hover {
          background-color: rgba(255, 255, 255, 0.3);
        }
      }

      img {
        width: 3rem;
        height: 3rem;
        object-fit: contain;
      }
    }

    .actions {
      position: absolute;
      bottom: 0;
      left: 0;

      display: flex;
      justify-content: flex-end;
      width: 100%;
      padding: 0 1rem;

      .btn {
        color: $customer-detail-text-color;

        background-color: rgba(255, 255, 255, 0);
        border-color: transparent;

        &:hover {
          background-color: rgba(255, 255, 255, 0.3);
        }
      }
    }
  }

  .chat-limit-warning {
    display: block;
    padding: 0.25rem 0.5rem;
    color: #f5a623;
    font-size: 12px;
  }

  // Card-details warning, two stages (BP-2970), rendered in .card-warning-slot above the footer.
  .card-warning-hint {
    display: flex;
    gap: 8px;
    align-items: center;
    padding: 7px 12px;
    color: #ffd75e;
    font-size: 13px;
    line-height: 17px;
    background: rgba($yellow, 0.14);
    border: 1px solid rgba($yellow, 0.45);
    border-radius: 0.25rem;

    svg {
      flex-shrink: 0;
    }
  }

  // Stacked, not side-by-side: in the customer panel the footer is ~350px wide, and an icon
  // column plus a button column left the body wrapping to eight lines.
  .card-warning-block {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 12px 14px;
    background: $customer-detail-bg;
    border: 1px solid $customer-detail-chat-divider-color;
    border-left: 3px solid $red;
    border-radius: 0.25rem;
    animation: card-warning-enter 200ms cubic-bezier(0, 0, 0.2, 1) both;

    .card-warning-head {
      display: flex;
      gap: 8px;
      align-items: center;
      color: #ff6fa8;

      svg {
        flex-shrink: 0;
      }
    }

    .card-warning-title {
      color: #fff;
      font-family: $accent-font-family;
      font-size: 15px;
      font-weight: 700;
      line-height: 18px;
    }

    .card-warning-body {
      margin: 0;
      color: #dfe6ee;
      font-size: 13px;
      line-height: 18px;
      text-wrap: pretty;
    }

    .card-warning-actions {
      display: flex;
      justify-content: flex-end;
    }

    .card-warning-dismiss {
      padding: 5px 14px;
      color: #fff;
      font-size: 13px;
      font-weight: 500;
      background: $button-blue;
      border: 1px solid $button-blue;
      border-radius: 0.25rem;

      &:hover {
        background: $hover-button-blue;
        border-color: $hover-button-blue;
      }
    }
  }

  .chat-input-blocked {
    box-shadow: inset 0 0 0 1px $red;
  }

  @keyframes card-warning-enter {
    from {
      opacity: 0;
      transform: translateY(6px);
    }

    to {
      opacity: 1;
      transform: none;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .card-warning-block {
      animation: none;
    }
  }

  .chat-input-disabled {
    background: transparent !important;
  }

  #chat-messages {
    $bg: darken($background, 2%);
    $dark-bg: darken($bg, 4%);
    max-height: 100%;
    overflow: auto;

    background: $customer-detail-light-bg;
    box-shadow: inset 0 0 30px #334048;

    content: '';

    img {
      width: 80%;
      height: 80px;
      object-fit: contain;
    }

    .message {
      width: 100%;
    }
  }
}
#chat-send {
  position: absolute;
  top: 50%;
  left: 100%;

  width: 2.5rem;
  height: 2.5rem;

  transform: translate(-50%, -50%);

  svg {
    margin-left: -1px;
  }
}

.hr-text {
  position: relative;

  width: 100%;
  height: 1.5em;

  line-height: 1em;
  text-align: center;

  border: 0;
  outline: 0;
  opacity: 0.5;

  &::before {
    position: absolute;
    top: 50%;
    left: 0;

    display: inline-block;
    width: 100%;
    height: 1px;

    background: linear-gradient(to right, transparent, white, transparent);

    content: '';
  }

  &::after {
    position: relative;

    display: inline-block;
    padding: 0 0.5em;

    color: white;
    line-height: 1.5em;

    background-color: $customer-detail-light-bg;

    content: attr(data-content);
  }
}
</style>
